import React from 'react';
import { getColumnStyle, getHeaderTooltipInfo, getNaviHeaderCellClasses } from '../../utils';
import { flexRender, RowData } from '@tanstack/react-table';
import SortArrowIcon from '@cp/src/assets/icons/SortArrowIcon';
import { SortOrder } from '@cp/src/pages/sensei/types';
import { isFunction } from '@cp/src/utils/commonUtils';
import { Typography } from '@navi/web-ui/lib/primitives';
import naviTableStyles from '../../NaviTable.module.scss';
import styles from './NaviTableHeader.module.scss';
import cx from 'classnames';
import { INaviTableHeader, NaviTableColumnDef } from '../../interfaces';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import { useNaviTableContext } from '../../context/NaviTableContext';

const NaviTableHeaderCell = <TData extends RowData>({ header }: INaviTableHeader<TData>) => {
  const isSortable = header.column.getCanSort();
  const cellValue = flexRender(header.column.columnDef.header, header.getContext());
  const sortDirection = header.column.getIsSorted();
  const tooltipInfo = getHeaderTooltipInfo(header);
  const { totalWidth, loading, sizeColumnsToFit = true } = useNaviTableContext<TData>();
  const columnDef = header?.column?.columnDef as NaviTableColumnDef<TData>;

  const handleHeaderCellClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (loading) {
      return;
    }
    if (isSortable) {
      const sortingHandler = header.column.getToggleSortingHandler();
      if (isFunction(sortingHandler)) {
        sortingHandler(e);
      }
    }
  };

  const getHeaderContent = () => {
    if (header.isPlaceholder) {
      return null;
    }
    if (tooltipInfo) {
      return (
        <Tooltip
          placement={tooltipInfo?.position || 'top'}
          hiddenPadding={tooltipInfo?.hiddenPadding || { top: 100 }}
          hideStrategy="referenceHidden"
        >
          <div className={getNaviHeaderCellClasses(header)} onClick={handleHeaderCellClick}>
            <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper inline">
              <Typography variant="h5" className={styles.headerCell}>
                {cellValue}
              </Typography>
            </TooltipTrigger>
            {isSortable ? (
              <div className="self-start">
                <SortArrowIcon sortDirection={sortDirection as SortOrder} />
              </div>
            ) : null}
          </div>
          <TooltipContent className={cx('tooltipWrapper', naviTableStyles.tooltip)}>
            {tooltipInfo.content}
          </TooltipContent>
        </Tooltip>
      );
    }
    return (
      <div className={getNaviHeaderCellClasses(header)} onClick={handleHeaderCellClick}>
        <Typography variant="h5" className={styles.headerCell}>
          {cellValue}
        </Typography>
        {isSortable ? (
          <div className="self-start">
            <SortArrowIcon sortDirection={sortDirection as SortOrder} />
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      style={getColumnStyle(header.column, totalWidth, columnDef?.cellStyle, sizeColumnsToFit)}
      className={styles.naviTableHeaderCell}
    >
      {getHeaderContent()}
    </div>
  );
};

export default NaviTableHeaderCell;
